import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import SavingsIcon from "@mui/icons-material/Savings";
import PercentIcon from "@mui/icons-material/Percent";
import CelebrationIcon from "@mui/icons-material/Celebration";
import FlagIcon from "@mui/icons-material/Flag";
import ExploreIcon from "@mui/icons-material/Explore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TimelineIcon from "@mui/icons-material/Timeline";
import PublicIcon from "@mui/icons-material/Public";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useNavigate } from "react-router-dom";

// ---- Brand Palette ----
const BRAND = {
  background: "#F3F6FB",
  primary: "#1E3A8A",
  secondary: "#7d9bdbff",
  accentGray: "#4B5563",
  accentGreen: "#10B981",
  danger: "#EF4444",
  paper: "#FFFFFF",
  border: "#E5E7EB",
};

// ---- Global keyframe styles injected once ----
const KEYFRAMES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.88); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes slideLeft {
    from { opacity: 0; transform: translateX(40px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  @keyframes countUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.05; transform: scale(1) rotate(var(--rot, 0deg)); }
    50%       { opacity: 0.10; transform: scale(1.07) rotate(var(--rot, 0deg)); }
  }
  @keyframes floatY {
    0%, 100% { transform: translateY(0px) rotate(var(--rot, 0deg)); }
    50%       { transform: translateY(-12px) rotate(var(--rot, 0deg)); }
  }
  @keyframes dividerGrow {
    from { width: 0; }
    to   { width: 120px; }
  }
  @keyframes heroLine {
    from { width: 0; opacity: 0; }
    to   { width: 80px; opacity: 1; }
  }

  .fade-up   { animation: fadeUp  0.65s cubic-bezier(0.22,1,0.36,1) both; }
  .fade-in   { animation: fadeIn  0.55s ease both; }
  .scale-in  { animation: scaleIn 0.55s cubic-bezier(0.22,1,0.36,1) both; }
  .slide-left{ animation: slideLeft 0.6s cubic-bezier(0.22,1,0.36,1) both; }

  /* stagger helpers */
  .d0  { animation-delay: 0ms; }
  .d1  { animation-delay: 80ms; }
  .d2  { animation-delay: 160ms; }
  .d3  { animation-delay: 240ms; }
  .d4  { animation-delay: 320ms; }
  .d5  { animation-delay: 400ms; }
  .d6  { animation-delay: 480ms; }
  .d7  { animation-delay: 560ms; }
  .d8  { animation-delay: 640ms; }

  /* scroll-triggered reveal */
  .reveal {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.65s cubic-bezier(0.22,1,0.36,1),
                transform 0.65s cubic-bezier(0.22,1,0.36,1);
  }
  .reveal.visible {
    opacity: 1;
    transform: translateY(0);
  }
  .reveal.visible.s1 { transition-delay: 80ms; }
  .reveal.visible.s2 { transition-delay: 160ms; }
  .reveal.visible.s3 { transition-delay: 240ms; }
  .reveal.visible.s4 { transition-delay: 320ms; }
  .reveal.visible.s5 { transition-delay: 400ms; }
  .reveal.visible.s6 { transition-delay: 480ms; }
`;

// ---- Inject keyframes ----
const StyleInjector: React.FC = () => {
  useEffect(() => {
    if (document.getElementById("home-keyframes")) return;
    const el = document.createElement("style");
    el.id = "home-keyframes";
    el.textContent = KEYFRAMES;
    document.head.appendChild(el);
  }, []);
  return null;
};

// ---- useReveal hook: IntersectionObserver for scroll reveals ----
function useReveal(rootMargin = "-60px") {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const items = el.querySelectorAll<HTMLElement>(".reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin }
    );
    items.forEach((item) => obs.observe(item));
    return () => obs.disconnect();
  }, [rootMargin]);
  return ref;
}

// ---- Loading splash ----
const LoadingSplash: React.FC<{ done: boolean }> = ({ done }) => (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: BRAND.primary,
      pointerEvents: done ? "none" : "all",
      opacity: done ? 0 : 1,
      transition: "opacity 0.55s cubic-bezier(0.4,0,0.2,1) 0.1s",
    }}
  >
    {/* Ripple rings */}
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          position: "absolute",
          width: 80 + i * 60,
          height: 80 + i * 60,
          borderRadius: "50%",
          border: `1.5px solid rgba(125,155,219,${0.45 - i * 0.12})`,
          animation: `pulse 2s ease-in-out ${i * 0.3}s infinite`,
        }}
      />
    ))}
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        background: `linear-gradient(135deg, ${BRAND.secondary}, #3b82f6)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 0 32px rgba(125,155,219,0.4)",
        mb: 3,
        animation: "scaleIn 0.5s cubic-bezier(0.22,1,0.36,1) both",
      }}
    >
      <AttachMoneyIcon sx={{ color: "#fff", fontSize: 32 }} />
    </Box>
    <Typography
      sx={{
        color: "rgba(255,255,255,0.9)",
        fontFamily: "'Playfair Display', serif",
        fontSize: "1.5rem",
        fontWeight: 700,
        letterSpacing: 1,
        animation: "fadeUp 0.6s 0.25s both",
      }}
    >
      AthEcoScope
    </Typography>
    <Typography
      sx={{
        color: "rgba(125,155,219,0.8)",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "0.8rem",
        letterSpacing: 3,
        textTransform: "uppercase",
        mt: 0.5,
        animation: "fadeUp 0.6s 0.4s both",
      }}
    >
      India · Economy · Data
    </Typography>

    {/* Loading bar */}
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        height: 3,
        background: `linear-gradient(90deg, transparent, ${BRAND.secondary}, #3b82f6, transparent)`,
        backgroundSize: "400px 3px",
        animation: "shimmer 1.2s linear infinite",
        width: "100%",
      }}
    />
  </Box>
);

// ---- StatCard Component ----
type StatCardProps = {
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle?: string;
  delay?: number;
};

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, subtitle, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: "-40px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const formatValue = (text: string) => (
    <span>
      {text.split(" ").map((part, i) => {
        if (part.includes("↑"))
          return <span key={i} style={{ fontWeight: 800, color: BRAND.accentGreen }}>↑</span>;
        if (part.includes("↓"))
          return <span key={i} style={{ fontWeight: 800, color: BRAND.danger }}>↓</span>;
        if (part.startsWith("+"))
          return <span key={i} style={{ fontWeight: 800, color: BRAND.accentGreen }}>{part} </span>;
        if (part === "131B")
          return <span key={i} style={{ fontWeight: 900, background: `linear-gradient(45deg, ${BRAND.secondary}, ${BRAND.primary})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{part}</span>;
        if (part === "₹199T")
          return <span key={i} style={{ fontWeight: 900, background: `linear-gradient(45deg, ${BRAND.accentGreen}, ${BRAND.secondary})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{part}</span>;
        return " " + part + " ";
      })}
    </span>
  );

  return (
    <div
      ref={ref}
      style={{
        height: "100%",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.96)",
        transition: `opacity 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      <Card
        elevation={0}
        sx={{
          height: "100%",
          borderRadius: 6,
          border: `1px solid ${BRAND.border}`,
          bgcolor: BRAND.paper,
          overflow: "hidden",
          position: "relative",
          transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: `0 16px 40px rgba(30,58,138,0.13)`,
            borderColor: BRAND.secondary,
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${BRAND.secondary}, ${BRAND.primary})`,
            transform: "scaleX(0)",
            transformOrigin: "left",
            transition: "transform 300ms ease",
          },
          "&:hover::before": { transform: "scaleX(1)" },
        }}
      >
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 3 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", fontSize: 28, color: BRAND.secondary, transition: "transform 200ms ease", ".MuiCard-root:hover &": { transform: "scale(1.15) rotate(-5deg)" } }}>
            {icon}
          </Box>
          <Typography variant="subtitle2" sx={{ fontSize: "0.78rem", color: BRAND.accentGray, letterSpacing: 1.2, textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif" }}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={900} sx={{ fontFamily: "'Playfair Display', serif", color: BRAND.primary, lineHeight: 1.2 }}>
            {formatValue(value)}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ fontStyle: "italic", color: BRAND.accentGray, fontFamily: "'DM Sans', sans-serif" }}>
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ---- InsightCard Component ----
type Insight = { icon: React.ReactNode; title: string; headline: string; detail?: string };

const InsightCard: React.FC<Insight & { delay?: number }> = ({ icon, title, headline, detail, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: "-40px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        height: "100%",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      <Card
        elevation={0}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 6,
          border: `1px solid ${BRAND.border}`,
          bgcolor: BRAND.paper,
          overflow: "hidden",
          position: "relative",
          transition: "transform 220ms ease, box-shadow 220ms ease, background-color 220ms ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: `0 12px 32px rgba(30,58,138,0.12)`,
            backgroundColor: "#f8faff",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0, left: 0, right: 0,
            height: 2,
            background: `linear-gradient(90deg, ${BRAND.secondary}, ${BRAND.accentGreen})`,
            transform: "scaleX(0)",
            transformOrigin: "left",
            transition: "transform 320ms ease",
          },
          "&:hover::after": { transform: "scaleX(1)" },
        }}
      >
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1, p: 3 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, color: BRAND.secondary }}>
            <Box sx={{ fontSize: 26, lineHeight: 0, transition: "transform 200ms ease", ".MuiCard-root:hover &": { transform: "scale(1.2) rotate(-8deg)" } }}>
              {icon}
            </Box>
            <Typography variant="subtitle2" sx={{ fontSize: "0.78rem", color: BRAND.accentGray, letterSpacing: 1.2, textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif" }}>
              {title}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={900} sx={{ fontFamily: "'Playfair Display', serif", color: BRAND.primary, lineHeight: 1.3 }}>
            {headline}
          </Typography>
          {detail && (
            <Typography variant="body2" sx={{ color: BRAND.accentGray, lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", fontSize: "0.875rem" }}>
              {detail}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ---- Insights Data ----
const insights: Insight[] = [
  { icon: <TrendingUpIcon />, title: "UPI Penetration", headline: "Urban dominates, Rural rising", detail: "Adoption started urban-first, but rural growth rates are catching up as infra and smartphone access improve." },
  { icon: <TimelineIcon />, title: "Velocity of Transactions", headline: "Small-ticket, high frequency", detail: "Micro-payments drive daily velocity; the tail of high-value transfers lifts total value disproportionately." },
  { icon: <PublicIcon />, title: "Financial Inclusion", headline: "Banking rails ≠ usage parity", detail: "Account openings outpaced consistent usage; nudges and UX still decide if new users become power users." },
  { icon: <StorefrontIcon />, title: "SME Digitization", headline: "QR rails → working capital", detail: "Digital receipts improve credit visibility, nudging lenders to price risk better for small businesses." },
  { icon: <SmartphoneIcon />, title: "Device Constraints", headline: "Low-end Android friction", detail: "App bloat and patchy data kill conversion. Lightweight flows win outside metros." },
  { icon: <LocationOnIcon />, title: "Regional Spread", headline: "State-level divergence matters", detail: "Policy, infra, and local ecosystems create stark gaps—plan analyses at the state/district level." },
];

// ---- Floating background icon ----
const FloatIcon: React.FC<{
  icon: React.ReactNode;
  size?: number;
  color?: string;
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;
  rot?: string;
  duration?: number;
}> = ({ icon, size = 140, color = BRAND.secondary, top, bottom, left, right, rot = "0deg", duration = 6 }) => (
  <Box
    sx={{
      position: "absolute",
      fontSize: size,
      color,
      opacity: 0.06,
      top, bottom, left, right,
      "--rot": rot,
      animation: `floatY ${duration}s ease-in-out infinite`,
      pointerEvents: "none",
      userSelect: "none",
    } as any}
  >
    {icon}
  </Box>
);

// ---- Section header ----
const SectionHeader: React.FC<{ title: string; delay?: number }> = ({ title, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: "-40px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ textAlign: "center", mb: 4 }}>
      <Typography
        variant="h5"
        fontWeight={900}
        sx={{
          color: BRAND.primary,
          fontFamily: "'Playfair Display', serif",
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(16px)",
          transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          height: 3,
          borderRadius: 2,
          background: `linear-gradient(90deg, ${BRAND.secondary}, ${BRAND.primary})`,
          mx: "auto",
          mt: 1.5,
          width: visible ? 120 : 0,
          transition: `width 0.6s cubic-bezier(0.22,1,0.36,1) ${delay + 120}ms`,
        }}
      />
    </Box>
  );
};

// ---- GoalItem Component (extracted to avoid hooks-in-map violation) ----
type GoalItemProps = {
  icon: React.ReactNode;
  title: string;
  body: string;
  delay?: number;
};

const GoalItem: React.FC<GoalItemProps> = ({ icon, title, body, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: "-40px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        {icon}
        <Typography variant="h5" fontWeight={900} sx={{ color: BRAND.primary, fontFamily: "'Playfair Display', serif" }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ lineHeight: 1.8, color: BRAND.accentGray, fontFamily: "'DM Sans', sans-serif", fontSize: "0.95rem" }}>
        {body}
      </Typography>
    </Box>
  );
};

// ============================= MAIN =============================
const Home: React.FC = () => {
  const navigate = useNavigate();
  const [splashDone, setSplashDone] = useState(false);
  const heroRef = useReveal("-20px");

  // Hide splash after ~1.6s
  useEffect(() => {
    const t = setTimeout(() => setSplashDone(true), 1600);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <StyleInjector />
      <LoadingSplash done={splashDone} />

      <Box
        sx={{
          bgcolor: BRAND.background,
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          fontFamily: "'DM Sans', sans-serif",
          opacity: splashDone ? 1 : 0,
          transition: "opacity 0.5s ease 0.1s",
        }}
      >
        {/* ===== HERO ===== */}
        <Box
          id="hero"
          component="section"
          ref={heroRef}
          sx={{
    mt: 0,
    pt: 0,   
            py: { xs: 8, sm: 11 },
            borderBottom: `1px solid ${BRAND.border}`,
            bgcolor: BRAND.paper,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Radial glow */}
          <Box sx={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse 70% 60% at 50% -10%, rgba(125,155,219,0.13) 0%, transparent 70%)`, pointerEvents: "none" }} />

          <FloatIcon icon={<AttachMoneyIcon fontSize="inherit" />} size={220} color={BRAND.primary} top={-60} left={-60} rot="-25deg" duration={7} />
          <FloatIcon icon={<ArrowUpwardIcon fontSize="inherit" />} size={130} color={BRAND.accentGreen} bottom={10} right={20} rot="10deg" duration={5} />
          <FloatIcon icon={<ArrowDownwardIcon fontSize="inherit" />} size={110} color={BRAND.danger} top={80} right={90} rot="-15deg" duration={8} />

          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", maxWidth: 860, mx: "auto" }}>
              {/* Eyebrow */}
              <Typography
                className="fade-in d0"
                variant="overline"
                sx={{
                  display: "inline-block",
                  color: BRAND.secondary,
                  letterSpacing: 3,
                  fontSize: "0.75rem",
                  fontFamily: "'DM Sans', sans-serif",
                  mb: 1.5,
                  px: 2,
                  py: 0.5,
                  borderRadius: 20,
                  border: `1px solid ${BRAND.border}`,
                  bgcolor: "#f0f5ff",
                }}
              >
                India · Economy · Data
              </Typography>

              {/* Main heading */}
              <Typography
                className="fade-up d1"
                variant="h3"
                fontWeight={900}
                gutterBottom
                sx={{
                  color: BRAND.primary,
                  fontFamily: "'Playfair Display', serif",
                  fontSize: { xs: "2rem", sm: "2.75rem", md: "3.25rem" },
                  lineHeight: 1.15,
                }}
              >
                Welcome to the{" "}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.secondary})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  AthEcoScope
                </Box>
              </Typography>

              {/* Animated underline */}
              <Box
                className="fade-in d2"
                sx={{
                  width: 80,
                  height: 3,
                  background: `linear-gradient(90deg, ${BRAND.secondary}, ${BRAND.primary})`,
                  borderRadius: 2,
                  mx: "auto",
                  mb: 3,
                }}
              />

              <Typography
                className="fade-up d3"
                variant="h6"
                sx={{ lineHeight: 1.7, color: BRAND.accentGray, fontWeight: 300, fontFamily: "'DM Sans', sans-serif", fontSize: { xs: "1rem", md: "1.15rem" } }}
              >
                Explore the data, numbers, trends, and impacts of several factors that affect the Indian economy, with special emphasis on UPI.
              </Typography>

              <Typography
                className="fade-up d4"
                variant="subtitle1"
                sx={{ mt: 2, color: BRAND.secondary, fontWeight: 500, maxWidth: 680, mx: "auto", fontStyle: "italic", letterSpacing: 0.3, fontFamily: "'Playfair Display', serif", fontSize: { xs: "0.95rem", md: "1.05rem" } }}
              >
                "UPI isn't just payments — it's reshaping consumption, credit, and financial inclusion across India."
              </Typography>

              {/* CTA row */}
              <Box className="fade-up d5" sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    bgcolor: BRAND.primary,
                    color: "#fff",
                    px: 4, py: 1.5,
                    borderRadius: 4,
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    boxShadow: `0 8px 24px rgba(30,58,138,0.22)`,
                    transition: "transform 200ms ease, box-shadow 200ms ease, background-color 200ms ease",
                    "&:hover": { bgcolor: "#152d70", transform: "translateY(-2px)", boxShadow: `0 12px 32px rgba(30,58,138,0.3)` },
                  }}
                >
                  Explore the Dashboard
                </Button>
                <Button
                onClick={() => navigate("/paper")}
                  variant="outlined"
                  sx={{
                    borderColor: BRAND.secondary,
                    color: BRAND.primary,
                    px: 4, py: 1.5,
                    borderRadius: 4,
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    transition: "transform 200ms ease, background-color 200ms ease",
                    "&:hover": { bgcolor: "#f0f5ff", transform: "translateY(-2px)", borderColor: BRAND.primary },
                  }}
                >
                  Read the Research
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ===== QUICK VIEW ===== */}
        <Box
          component="section"
          sx={{ py: { xs: 6, sm: 8 }, flexGrow: 1, position: "relative" }}
        >
          <FloatIcon icon={<AttachMoneyIcon fontSize="inherit" />} size={160} color={BRAND.secondary} top={40} right={-30} duration={9} />
          <FloatIcon icon={<ArrowUpwardIcon fontSize="inherit" />} size={110} color={BRAND.accentGreen} bottom={20} left={10} rot="15deg" duration={6} />

          <Container maxWidth="lg">
            <SectionHeader title="Quick View" delay={0} />
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <StatCard icon={<SavingsIcon />} title="Total UPI Transactions (2024)" value="131B • ₹199T" subtitle="Volume • Value" delay={0} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard icon={<PercentIcon />} title="YoY Growth Rate" value="+46%" subtitle="2023 → 2024 (volume)" delay={120} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard icon={<CelebrationIcon />} title="Seasonality Snapshot" value="Diwali ↑ • Feb ↓" subtitle="Spikes & dips visible in monthly data" delay={240} />
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* ===== INSIGHTS ===== */}
        <Box
          component="section"
          sx={{ py: { xs: 6, sm: 8 }, borderTop: `1px solid ${BRAND.border}`, bgcolor: BRAND.paper, position: "relative", overflow: "hidden" }}
        >
          <FloatIcon icon={<ArrowDownwardIcon fontSize="inherit" />} size={160} color={BRAND.danger} top={0} left={-50} rot="10deg" duration={7} />
          <FloatIcon icon={<ArrowUpwardIcon fontSize="inherit" />} size={130} color={BRAND.accentGreen} bottom={0} right={0} duration={5} />

          <Container maxWidth="lg">
            <SectionHeader title="Insights" delay={0} />
            <Grid container spacing={3}>
              {insights.map((ins, idx) => (
                <Grid key={idx} item xs={12} sm={6} md={4}>
                  <InsightCard {...ins} delay={idx * 80} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ===== PROJECT GOALS ===== */}
        <Box
          component="section"
          sx={{ py: { xs: 7, sm: 9 }, borderTop: `1px solid ${BRAND.border}`, bgcolor: BRAND.paper, position: "relative", overflow: "hidden" }}
        >
          <FloatIcon icon={<AttachMoneyIcon fontSize="inherit" />} size={190} color={BRAND.secondary} top={40} right={-50} rot="-25deg" duration={10} />

          <Container maxWidth="lg">
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <GoalItem
                  icon={<FlagIcon sx={{ mr: 1, fontSize: 28, color: BRAND.secondary }} />}
                  title="Project Goals"
                  body="This project aims to simplify the understanding of India's rapidly changing financial landscape by using data-driven insights. The ultimate goal is to make economic research interactive, engaging, and accessible for everyone."
                  delay={0}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <GoalItem
                  icon={<ExploreIcon sx={{ mr: 1, fontSize: 28, color: BRAND.secondary }} />}
                  title="What You'll Know"
                  body="By exploring this platform, you will learn how UPI transformed India's payment systems, discover regional inequalities, understand the stock market's growing accessibility, and uncover how these economic shifts affect everyday lives."
                  delay={160}
                />
              </Grid>
            </Grid>

            {/* CTA */}
            <Box sx={{ textAlign: "center", mt: 7 }}>
              <Button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                variant="contained"
                sx={{
                  bgcolor: BRAND.secondary,
                  color: "#072540",
                  px: 5,
                  py: 1.75,
                  borderRadius: 4,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  letterSpacing: 0.3,
                  boxShadow: `0 8px 24px rgba(125,155,219,0.3)`,
                  transition: "transform 200ms ease, box-shadow 200ms ease, background-color 200ms ease",
                  "&:hover": {
                    bgcolor: BRAND.primary,
                    color: "#fff",
                    transform: "translateY(-3px)",
                    boxShadow: `0 14px 36px rgba(30,58,138,0.25)`,
                  },
                }}
              >
                Dive Into The Data ↑
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Home;